import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DIDAvatar, { DIDAvatarHandle } from '../components/DIDAvatar/DIDAvatar';
import AIService from '../services/AIService';
import { Mic, MicOff, Keyboard, Square, FileSpreadsheet, Download, LogOut, Eye, EyeOff, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AuthService from '../services/AuthService';
import { useNavigate } from 'react-router-dom';
import AnalogClock from '../components/AnalogClock/AnalogClock';
import { 
  MobileHeader, 
  MobileLogoContainer, 
  MobileTitle, 
  MobileTitleMain, 
  MobileTitleSub, 
  SkyLogoImg, 
  MobileHeaderButtons 
} from '../styles/MobileStyles';

const NMPLogo = require('../Images/NMP.png');
const SkyLogo = require('../Images/skyangel.png');

const InteractiveAvatarPage: React.FC = () => {
  const navigate = useNavigate();
  const avatarRef = useRef<DIDAvatarHandle>(null);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [inputText, setInputText] = useState('');
  const [avatarReady, setAvatarReady] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [downloadLink, setDownloadLink] = useState<string | null>(null);
  const [showTextBoxes, setShowTextBoxes] = useState(false);
  const recognitionRef = useRef<any>(null);

  React.useEffect(() => {
    // Set initial greeting with user name
    const user = AuthService.getCurrentUser();
    const userName = user?.name || user?.username || 'Usuario';
    console.log('Current user:', user);
    console.log('User name for greeting:', userName);
    setCurrentResponse(`¡Hola ${userName}! Soy tu asistente virtual de Nacional Monte de Piedad. Presiona el micrófono y háblame sobre servicios, préstamos, subastas o cualquier consulta que tengas.`);
    
    const timer = setTimeout(() => {
      if (avatarRef.current && !avatarReady) {
        avatarRef.current.startSession();
      }
    }, 1000);
    
    return () => {
      clearTimeout(timer);
      if (avatarRef.current) {
        avatarRef.current.endSession();
      }
    };
  }, []);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    
    const handleBeforeUnload = () => {
      if (avatarRef.current) {
        avatarRef.current.endSession();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      clearInterval(timer);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const formatDateTime = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hour = date.getHours().toString().padStart(2, '0');
    const minute = date.getMinutes().toString().padStart(2, '0');
    const second = date.getSeconds().toString().padStart(2, '0');
    
    return `${hour}:${minute}:${second}\n${day}/${month}/${year}`;
  };

  const formatFullDateTime = (date: Date) => {
    return date.toLocaleString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };



  const handleLogout = () => {
    AuthService.logout();
    window.location.href = '/login';
  };

  const startListening = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'es-MX';
      
      recognitionRef.current.onstart = () => {
        setIsListening(true);
        setCurrentResponse('Te escucho... Puedes preguntarme sobre servicios de Nacional Monte de Piedad.');
      };
      
      recognitionRef.current.onresult = async (event: any) => {
        const text = event.results[0][0].transcript;
        setIsListening(false);
        await processQuery(text);
      };
      
      recognitionRef.current.onerror = () => {
        setIsListening(false);
        setCurrentResponse('Error al escuchar. Verifica los permisos del micrófono.');
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.start();
    } else {
      alert('Tu navegador no soporta reconocimiento de voz');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  };

  const handleTextSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputText.trim() && !isProcessing) {
      const query = inputText.trim();
      setInputText('');
      setShowTextInput(false);
      await processQuery(query);
    }
  };

  const stopSpeaking = () => {
    setIsSpeaking(false);
    if (avatarRef.current) {
      avatarRef.current.stopSpeaking();
    }
  };

  const processQuery = async (text: string) => {
    setIsProcessing(true);
    setCurrentResponse(`🤔 Analizando: "${text}"`);
    setDownloadLink(null);
    
    let accumulatedText = '';
    
    try {
      const response = await AIService.sendMCPVoiceQuery(text, true, (chunk) => {
        accumulatedText += chunk;
        setCurrentResponse(accumulatedText);
      });
      
      if (response.success && response.ai_response) {
        if (response.download_link) {
          setDownloadLink(response.download_link);
        }
        
        setIsProcessing(false);
        
        if (avatarRef.current && response.ai_response) {
          avatarRef.current.speak(response.ai_response);
        }
      } else {
        setCurrentResponse('Error al procesar la consulta. Intenta nuevamente.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setCurrentResponse('Error al procesar la consulta. Verifica tu conexión.');
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!downloadLink) return;
    
    try {
      await AIService.downloadExcelReport(downloadLink);
    } catch (error) {
      console.error('Error descargando archivo:', error);
      alert('Error al descargar el archivo');
    }
  };

  return (
    <AvatarContainer>
      <MobileHeader $textVisible={showTextBoxes}>
        <MobileLogoContainer>
          <SkyLogoImg src={SkyLogo} alt="Sky" />
          <NMPLogoImg src={NMPLogo} alt="Nacional Monte de Piedad" />
          <MobileTitle>
            <MobileTitleMain>Piedad</MobileTitleMain>
            <MobileTitleSub>Asistente Virtual</MobileTitleSub>
          </MobileTitle>
        </MobileLogoContainer>
        <MobileHeaderButtons>
          <LogoutButton onClick={handleLogout} title="Cerrar sesión">
            <LogOut size={20} />
          </LogoutButton>
        </MobileHeaderButtons>
      </MobileHeader>
      
      <MainContent>
        <CenterPanel>
          <HeaderOverlay $isVisible={showTextBoxes}>
            <HeaderBox>
              <LogoContainer>
                <NMPLogoImg src={NMPLogo} alt="Nacional Monte de Piedad" />
                <div>
                  <HeaderTitle>Piedad</HeaderTitle>
                  <HeaderSubtitle2>Asistente Virtual</HeaderSubtitle2>
                </div>
              </LogoContainer>
              <HeaderSubtitle>Nacional Monte de Piedad — Programa auto avanza</HeaderSubtitle>
            </HeaderBox>
            
            <ResponseOverlay>
              <ResponseBox>
                <ScrollableContent>
                  <ResponseText><ReactMarkdown remarkPlugins={[remarkGfm]}>{currentResponse}</ReactMarkdown></ResponseText>
                  {isSpeaking && <SpeakingIndicator>Hablando...</SpeakingIndicator>}
                  
                  {downloadLink && (
                    <DownloadSection>
                      <DownloadButton
                        as={motion.button}
                        onClick={handleDownload}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FileSpreadsheet size={20} />
                        <span>Descargar Reporte Excel</span>
                        <Download size={16} />
                      </DownloadButton>
                      <DownloadHint>
                        Reporte generado con datos actualizados
                      </DownloadHint>
                    </DownloadSection>
                  )}
                </ScrollableContent>
              </ResponseBox>
            </ResponseOverlay>
          </HeaderOverlay>
          
          <TopRightContainer>
            <ToggleTextButton 
              onClick={() => setShowTextBoxes(!showTextBoxes)}
              title={showTextBoxes ? "Ocultar texto" : "Mostrar texto"}
              style={{ display: 'none' }}
            >
              {showTextBoxes ? <EyeOff size={16} /> : <Eye size={16} />}
            </ToggleTextButton>
            {/* Botón para ir al chat de solo texto */}
            <TextChatBtn onClick={() => navigate('/text-chat')} title="Chat de texto">
              <MessageSquare size={20} />
            </TextChatBtn>
            <DateTimeContainer>
              <ClockSection>
                <AnalogClock size={32} />
                <TimeText>
                  <span className="short-date">{formatDateTime(currentDateTime)}</span>
                  <span className="full-date">{formatFullDateTime(currentDateTime)}</span>
                </TimeText>
              </ClockSection>
            </DateTimeContainer>
            <LogoutButton onClick={handleLogout} title="Cerrar sesión">
              <LogOut size={20} />
            </LogoutButton>
          </TopRightContainer>
          
          <DIDAvatar 
            ref={avatarRef}
            onReady={() => setAvatarReady(true)}
            onError={(error) => console.error('Error:', error)}
            onSpeakingStart={() => setIsSpeaking(true)}
            onSpeakingEnd={() => setIsSpeaking(false)}
          />
          
          <ResponseOverlay>
            <ResponseBox>
              <ResponseText><ReactMarkdown remarkPlugins={[remarkGfm]}>{currentResponse}</ReactMarkdown></ResponseText>
              {isSpeaking && <SpeakingIndicator>Hablando...</SpeakingIndicator>}
              
              {downloadLink && (
                <DownloadSection>
                  <DownloadButton
                    as={motion.button}
                    onClick={handleDownload}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FileSpreadsheet size={20} />
                    <span>Descargar Reporte Excel</span>
                    <Download size={16} />
                  </DownloadButton>
                  <DownloadHint>
                    Reporte generado con datos actualizados
                  </DownloadHint>
                </DownloadSection>
              )}
            </ResponseBox>
          </ResponseOverlay>
          
          <VideoControls>
            {(isProcessing) && (
              <StatusMessage>
                {'Procesando...'}
              </StatusMessage>
            )}
            <ButtonContainer>
              <ControlButton
                onClick={isSpeaking ? stopSpeaking : (isListening ? stopListening : startListening)}
                disabled={isProcessing}
                $isActive={isListening || isSpeaking}
                $isSpeaking={isSpeaking}
                title={isSpeaking ? "Detener conversación" : (isListening ? "Detener micrófono" : "Iniciar micrófono")}
              >
                {isSpeaking ? <Square size={32} /> : (isListening ? <MicOff size={32} /> : <Mic size={32} />)}
              </ControlButton>
              
              <ControlButton 
                onClick={() => setShowTextInput(!showTextInput)} 
                disabled={isProcessing}
                title="Escribir mensaje de texto"
              >
                <Keyboard size={32} />
              </ControlButton>

              <ToggleTextButton 
                onClick={() => setShowTextBoxes(!showTextBoxes)}
                title={showTextBoxes ? "Ocultar texto" : "Mostrar texto"}
              >
                {showTextBoxes ? <EyeOff size={24} /> : <Eye size={24} />}
              </ToggleTextButton>
            </ButtonContainer>
          </VideoControls>
        </CenterPanel>
      </MainContent>

      <AnimatePresence>
        {showTextInput && (
          <TextInputModal
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <TextInputContainer>
              <TextInputTitle>Escribe tu consulta</TextInputTitle>
              <form onSubmit={handleTextSubmit}>
                <TextInputField
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Escribe aquí..."
                  autoFocus
                />
                <TextInputButtons>
                  <TextInputCancel type="button" onClick={() => {
                    setInputText('');
                    setShowTextInput(false);
                  }}>
                    Cancelar
                  </TextInputCancel>
                  <TextInputSend type="submit" disabled={!inputText.trim()}>
                    Preguntar
                  </TextInputSend>
                </TextInputButtons>
              </form>
            </TextInputContainer>
          </TextInputModal>
        )}
      </AnimatePresence>
    </AvatarContainer>
  );
};

const AvatarContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: transparent;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  height: 100vh;
`;

const CenterPanel = styled.div`
  flex: 1;
  position: relative;
`;

const HeaderOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 40px;
  left: 40px;
  background: transparent;
  padding: 0;
  height: auto;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    position: absolute;
    top: 100px;
    left: 10px;
    right: 10px;
    width: auto;
    margin: 0 10px;
    display: ${props => props.$isVisible ? 'flex' : 'none'};
  }
`;

const HeaderBox = styled.div`
  background: rgba(240, 240, 240, 0.4);
  border: 2px solid rgba(180, 180, 180, 0.4);
  border-radius: 15px 15px 0 0;
  padding: 25px;
  backdrop-filter: blur(25px);
  width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    display: none;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ResponseOverlay = styled.div`
  width: 400px;
  max-height: calc(100vh - 200px);
  z-index: 10;
  min-height: 0;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    width: 100%;
    max-height: calc(100vh - 300px);
    margin-bottom: 20px;
    margin-top: 0;
  }
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  z-index: 10;
`;

const StatusMessage = styled.div`
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 14px;
  backdrop-filter: blur(5px);
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const ScrollableContent = styled.div`
  overflow-y: auto;
  max-height: calc(100vh - 290px);
  margin: -5px;
  padding: 5px;

  /* Scroll elegante semitransparente */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(180, 180, 180, 0.6);
    border-radius: 10px;
    backdrop-filter: blur(10px);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(160, 160, 160, 0.8);
  }

  @media (max-width: 768px) {
    max-height: calc(100vh - 350px);
  }
`;

const TopRightContainer = styled.div`
  position: absolute;
  top: 15px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10;

  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    flex-direction: column;
    gap: 5px;
    
    button:last-child {
      display: none;
    }
  }
`;

const DateTimeContainer = styled.div`
  color: #000000;
  font-size: 0.8rem;
  font-weight: 500;
  background: rgba(240, 240, 240, 0.4);
  padding: 6px 8px;
  border-radius: 8px;
  border: 2px solid rgba(180, 180, 180, 0.4);
  backdrop-filter: blur(25px);
  cursor: pointer;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ClockSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TimeText = styled.div`
  white-space: pre-line;
  text-align: left;
  font-family: 'Segoe UI', 'Roboto', 'Arial', sans-serif;
  font-weight: 600;
  letter-spacing: 0.5px;
  
  .full-date {
    display: none;
  }
  
  .short-date {
    display: block;
  }
  
  ${DateTimeContainer}:hover & {
    .short-date {
      display: none;
    }
    
    .full-date {
      display: block;
    }
  }
`;

// Botón para ir al chat de solo texto (al lado del reloj)
const TextChatBtn = styled.button`
  color: rgba(255, 255, 255, 0.9);
  background: rgba(152, 1, 46, 0.3);
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(152, 1, 46, 0.5);
  backdrop-filter: blur(25px);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(152, 1, 46, 0.5);
    transform: translateY(-1px);
  }
`;

const LogoutButton = styled.button`
  color: rgba(255, 255, 255, 0.9);
  background: rgba(220, 20, 60, 0.3);
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(220, 20, 60, 0.5);
  backdrop-filter: blur(25px);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(220, 20, 60, 0.5);
    transform: translateY(-1px);
  }
`;

const NMPLogoImg = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  background: transparent;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    background: transparent;
  }
`;

const HeaderTitle = styled.h1`
  color: #000000;
  font-size: 24px;
  font-weight: 700;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const HeaderSubtitle = styled.p`
  color: #000000;
  font-size: 14px;
  margin: 0;
  font-weight: 700;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const HeaderSubtitle2 = styled.p`
  color: #000000;
  font-size: 16px;
  margin: 0;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ResponseBox = styled.div`
  background: rgba(240, 240, 240, 0.4);
  border: 2px solid rgba(180, 180, 180, 0.4);
  border-radius: 0 0 15px 15px;
  padding: 25px;
  backdrop-filter: blur(25px);
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    padding: 15px;
    border-radius: 0 0 10px 10px;
  }
`;

const ResponseText = styled.div`
  color: #000000;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  margin: 0;

  h1, h2, h3 { margin: 8px 0 4px; font-weight: 700; }
  h2 { font-size: 18px; }
  h3 { font-size: 16px; }
  p { margin: 4px 0; }
  ul, ol { margin: 4px 0; padding-left: 20px; }
  table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 14px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
  th { background: #f5f5f5; font-weight: 700; }
  strong { font-weight: 700; }
  code { background: #f0f0f0; padding: 2px 4px; border-radius: 3px; font-size: 14px; }
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SpeakingIndicator = styled.div`
  margin-top: 15px;
  padding: 10px 15px;
  background: rgba(255, 215, 0, 0.2);
  border: 1px solid rgba(255, 215, 0, 0.4);
  border-radius: 8px;
  color: #FFD700;
  font-size: 14px;
  text-align: center;
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
`;

const ControlButton = styled.button<{ $isActive?: boolean; $isSpeaking?: boolean }>`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 2px solid ${props => props.$isSpeaking ? '#FF4444' : (props.$isActive ? '#FF4444' : 'rgba(255, 255, 255, 0.3)')};
  background: ${props => props.$isSpeaking ? 'rgba(255, 68, 68, 0.2)' : (props.$isActive ? 'rgba(255, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.1)')};
  color: ${props => props.$isSpeaking ? '#FF4444' : (props.$isActive ? '#FF4444' : '#FFFFFF')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.$isSpeaking ? 'rgba(255, 68, 68, 0.3)' : (props.$isActive ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.2)')};
    border-color: ${props => props.$isSpeaking ? '#FF4444' : (props.$isActive ? '#FF4444' : 'rgba(255, 255, 255, 0.5)')};
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const TextInputModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const TextInputContainer = styled.div`
  background: rgba(240, 240, 240, 0.6);
  border: 2px solid rgba(120, 120, 120, 0.6);
  border-radius: 15px;
  padding: 30px;
  width: 90%;
  max-width: 500px;
  backdrop-filter: blur(25px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    padding: 20px;
    width: 95%;
    margin: 0 10px;
  }
`;

const TextInputTitle = styled.h3`
  color: #000000;
  margin: 0 0 20px 0;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
`;

const TextInputField = styled.input`
  width: 100%;
  padding: 15px;
  border: 2px solid rgba(120, 120, 120, 0.6);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: #000000;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 20px;
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 700;
  }
  
  &:focus {
    outline: none;
    border-color: rgba(120, 120, 120, 0.8);
  }
`;

const TextInputButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const TextInputCancel = styled.button`
  padding: 12px 24px;
  border: 2px solid rgba(120, 120, 120, 0.6);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #000000;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:focus {
    outline: none;
  }
`;

const TextInputSend = styled.button`
  padding: 12px 24px;
  border: 2px solid rgba(120, 120, 120, 0.6);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #000000;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:focus {
    outline: none;
  }
`;

const ProcessingOverlay = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 16px;
  z-index: 999;
`;

// Estilos para descarga de Excel
const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(220, 20, 60, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(220, 20, 60, 0.6);
  }
`;

const DownloadSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 215, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DownloadButton = styled.button`
  background: linear-gradient(135deg, #DC143C, #B22222);
  color: #FFFFFF;
  border: 2px solid #FFFFFF;
  border-radius: 8px;
  padding: 12px 20px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  animation: ${glow} 3s ease-in-out infinite;
  
  &:hover {
    background: linear-gradient(135deg, #B22222, #8B0000);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(220, 20, 60, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const DownloadHint = styled.div`
  color: #000000ff;
  font-size: 12px;
  text-align: center;
  font-style: italic;
`;

const ToggleTextButton = styled.button`
  color: #FFFFFF;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  backdrop-filter: blur(25px);
  cursor: pointer;
  transition: all 0.3s ease;
  display: none;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    display: flex;
    width: 60px;
    height: 60px;
  }
`;

export default InteractiveAvatarPage;
